import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const [productsData, setProductsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("nymphea_cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProductsData(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Erreur lors du chargement des produits");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const addToCart = (product) => {
    const existing = cart.find((item) => item.product_id === product.id);
    let newCart;
    
    if (existing) {
      newCart = cart.map((item) =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      newCart = [...cart, { product_id: product.id, quantity: 1, product }];
    }
    
    setCart(newCart);
    localStorage.setItem("nymphea_cart", JSON.stringify(newCart));
    toast.success("Ajouté au panier !");
  };

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  const filteredProducts =
    category === "all"
      ? productsData
      : productsData.filter((p) => p.categorie === category);

  return (
    <div data-testid="shop-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
                Shop
              </p>
              <h1 className="page-title animate-fade-in-up animation-delay-100">
                Boutique
              </h1>
              <p className="page-subtitle animate-fade-in-up animation-delay-200">
                Prints, stickers et autres créations.
              </p>
            </div>
            {cartCount > 0 && (
              <Link to="/checkout" data-testid="cart-link">
                <Button variant="outline" className="relative border-foreground text-foreground hover:bg-foreground hover:text-background">
                  <ShoppingBag className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Panier
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center">
                    {cartCount}
                  </span>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Categories - Only prints for now */}
      <section className="pb-8">
        <div className="page-container">
          <div className="flex gap-3">
            <button
              onClick={() => setCategory("all")}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-all border ${
                category === "all"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-transparent text-foreground border-border hover:border-foreground"
              }`}
              data-testid="filter-all"
            >
              Tout
            </button>
            <button
              onClick={() => setCategory("prints")}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-all border ${
                category === "prints"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-transparent text-foreground border-border hover:border-foreground"
              }`}
              data-testid="filter-prints"
            >
              Prints
            </button>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group"
                data-testid={`product-card-${product.id}`}
              >
                <div className="img-hover-zoom overflow-hidden mb-4 border border-border/40">
                  <img
                    src={product.image_url}
                    alt={product.nom}
                    className="w-full aspect-square object-cover"
                  />
                </div>
                <h3 className="font-medium mb-1 group-hover:text-muted-foreground transition-colors">
                  {product.nom}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {product.prix.toFixed(2)}€
                </p>
                <Button
                  onClick={() => addToCart(product)}
                  variant="outline"
                  className="w-full text-xs border-foreground text-foreground hover:bg-foreground hover:text-background"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  Ajouter au panier
                </Button>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">Aucun produit dans cette catégorie.</p>
            </div>
          )}
        </div>
      </section>

      {/* Info */}
      <section className="py-16 border-t border-border/40 bg-secondary/30">
        <div className="page-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto text-center">
            <div>
              <h3 className="font-heading text-lg mb-2">Envoi postal</h3>
              <p className="text-sm text-muted-foreground">
                Livraison partout en France sous 5-7 jours ouvrés.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-lg mb-2">Remise en main propre</h3>
              <p className="text-sm text-muted-foreground">
                Gratuit — Récupérez votre commande directement à l'Atelier La Peauésie.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
